
import React, { useState } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import type { Theme } from '../types';
import { useAuth } from '../hooks/useAuth';
import { syncFolderStructure, initializeOrderIndices } from '../services/firebaseService';
import toast from 'react-hot-toast';
import Spinner from './Spinner';
import { useDialog } from '../contexts/DialogContext';

const themes: { id: Theme; name: string; colors: string[] }[] = [
    { id: 'dark', name: '다크 모드', colors: ['#18181B', '#27272A', '#881d3a'] },
    { id: 'light', name: '라이트 모드', colors: ['#FFFFFF', '#F4F4F5', '#881d3a'] },
];

const SettingsView: React.FC = () => {
    const { 
        koreanFontSize, 
        setKoreanFontSize, 
        mathFontSize, 
        setMathFontSize,
        lineHeight,
        setLineHeight,
        theme,
        setTheme,
    } = useSettings();

    const { user } = useAuth();
    const dialog = useDialog();
    
    const [isInitializing, setIsInitializing] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);

    const handleInitializeOrder = async () => {
        if (!user) return;
        const confirmed = await dialog.confirm("기존의 모든 문제에 정렬을 위한 순서 번호(Order Index)를 부여합니다.\n\n진행하시겠습니까? (시간이 다소 소요될 수 있습니다.)");
        if (confirmed) {
            setIsInitializing(true);
            try {
                await initializeOrderIndices(user.uid);
                toast.success("정렬 초기화가 완료되었습니다. 이제 드래그로 순서를 변경할 수 있습니다.");
            } catch (e) {
                console.error(e);
                toast.error("초기화 중 오류가 발생했습니다.");
            } finally {
                setIsInitializing(false);
            }
        }
    };

    const handleSyncFolders = async () => {
        if (!user) return;
        const confirmed = await dialog.confirm("전체 문제를 검사하여 누락된 폴더 구조를 복구합니다.\n\n문제가 보이지 않거나 폴더가 목록에 없을 때 유용합니다.\n진행하시겠습니까?");
        if (confirmed) {
            setIsSyncing(true);
            try {
                const count = await syncFolderStructure(user.uid);
                toast.success(`동기화 완료! 총 ${count}개의 폴더를 확인했습니다.`);
            } catch (e) {
                console.error(e);
                toast.error("동기화 중 오류가 발생했습니다.");
            } finally {
                setIsSyncing(false);
            }
        }
    };

    return (
        <div className="space-y-6">
          <div>
            <label htmlFor="korean-font-size-slider" className="block text-sm font-medium text-[var(--color-text-secondary)]">
              한글 폰트 크기: <span className="font-bold text-[var(--color-text-primary)]">{koreanFontSize.toFixed(1)}pt</span>
            </label>
            <input
              id="korean-font-size-slider"
              type="range"
              min="8"
              max="18"
              step="0.1"
              value={koreanFontSize}
              onChange={(e) => setKoreanFontSize(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[var(--color-accent)]"
            />
          </div>
          
          <div>
            <label htmlFor="math-font-size-slider" className="block text-sm font-medium text-[var(--color-text-secondary)]">
              수식 폰트 크기: <span className="font-bold text-[var(--color-text-primary)]">{mathFontSize.toFixed(1)}pt</span>
            </label>
            <input
              id="math-font-size-slider"
              type="range"
              min="8"
              max="18"
              step="0.1"
              value={mathFontSize}
              onChange={(e) => setMathFontSize(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[var(--color-accent)]"
            />
          </div>

          <div>
            <label htmlFor="line-height-slider" className="block text-sm font-medium text-[var(--color-text-secondary)]">
              줄 간격: <span className="font-bold text-[var(--color-text-primary)]">{lineHeight.toFixed(2)}</span>
            </label>
            <input
              id="line-height-slider"
              type="range"
              min="1.2"
              max="2.2"
              step="0.05"
              value={lineHeight}
              onChange={(e) => setLineHeight(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[var(--color-accent)]"
            />
          </div>

          <div>
             <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                앱 테마
            </label>
            <div className="grid grid-cols-2 gap-3">
                {themes.map((t) => (
                    <button 
                        key={t.id}
                        onClick={() => setTheme(t.id)}
                        className={`p-2 rounded-lg border-2 transition-all ${theme === t.id ? 'border-[var(--color-accent)] scale-105' : 'border-transparent hover:border-gray-500'}`}
                        style={{backgroundColor: t.colors[1]}}
                    >
                        <div className="flex justify-center items-center space-x-1 mb-2">
                            {t.colors.map((color, i) => (
                                <div key={i} className="w-5 h-5 rounded-full border border-black/10" style={{ backgroundColor: color }}></div>
                            ))}
                        </div>
                        <span className="text-xs" style={{color: t.id === 'dark' ? '#F4F4F5' : '#18181B' }}>{t.name}</span>
                    </button>
                ))}
            </div>
          </div>

          <div className="pt-4 border-t border-[var(--color-border)] space-y-3">
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                  고급 설정
              </label>
              <button 
                  onClick={handleInitializeOrder} 
                  disabled={isInitializing}
                  className="w-full bg-indigo-900/50 border border-indigo-500/50 text-indigo-200 font-bold py-2 px-4 rounded-lg hover:bg-indigo-800/50 transition-colors disabled:opacity-50"
              >
                  {isInitializing ? <div className="flex justify-center items-center gap-2"><Spinner small /> 처리 중...</div> : 'DB 정렬 초기화 (드래그 정렬 활성화)'}
              </button>
              
              <button 
                  onClick={handleSyncFolders} 
                  disabled={isSyncing}
                  className="w-full bg-emerald-900/50 border border-emerald-500/50 text-emerald-200 font-bold py-2 px-4 rounded-lg hover:bg-emerald-800/50 transition-colors disabled:opacity-50"
              >
                  {isSyncing ? <div className="flex justify-center items-center gap-2"><Spinner small /> 동기화 중...</div> : '폴더 구조 동기화 (사라진 폴더 복구)'}
              </button>
              
              <p className="text-[10px] text-[var(--color-text-tertiary)] mt-1">
                  * 문제가 보이지 않거나 폴더가 목록에 없을 때 실행하세요.
              </p>
          </div>
        </div>
    );
};

export default SettingsView;
